import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;

// Configure axios defaults
axios.defaults.withCredentials = true;

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = not authenticated
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/api/auth/me`, {
                withCredentials: true
            });
            setUser(response.data);
        } catch (error) {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        const response = await axios.post(`${API}/api/auth/login`, 
            { email, password },
            { withCredentials: true }
        );
        setUser(response.data);
        return response.data;
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setUser(false);
        }
    };

    const refreshToken = async () => {
        try {
            await axios.post(`${API}/api/auth/refresh`, {}, { withCredentials: true });
            await checkAuth();
            return true;
        } catch (error) {
            setUser(false);
            return false;
        }
    };

    const value = {
        user,
        loading,
        login,
        logout,
        refreshToken,
        checkAuth,
        refreshUser: checkAuth,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        isLogistica: user?.role === 'logistica',
        isStoreAgent: user?.role === 'store_agent',
        isStoreManager: user?.role === 'responsabile_store',
        canAccessInventory: user?.role === 'admin' || user?.role === 'logistica',
        canAccessCatalog: user?.role === 'admin' || user?.role === 'logistica',
        canAccessStore: user?.role === 'admin' || user?.role === 'store_agent' || user?.role === 'responsabile_store',
        canManageStoreCatalog: user?.role === 'admin' || user?.role === 'responsabile_store'
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
